import React from 'react'
import { Button } from '../ui/Button'
import { apiFetch, ApiError } from '../../lib/api-client'

export type CustomFieldType = 'text' | 'number' | 'date' | 'boolean'

interface FieldValue {
  definition_id: string
  label: string
  field_type: CustomFieldType
  sort_order: number
  value: string | boolean | null
}

// Draft values are held as strings in the form; booleans as 'true' | 'false' | ''.
function toDraft(v: FieldValue): string {
  if (v.value === null || v.value === undefined) return ''
  if (typeof v.value === 'boolean') return String(v.value)
  // Numbers arrive as "1234.5" strings; show Danish decimal comma (string op only).
  return v.field_type === 'number' ? v.value.replace('.', ',') : v.value
}

// Digits with optional single decimal separator (comma or point). Thousand separators are rejected.
const NUMBER_RE = /^-?\d+([.,]\d+)?$/
const NUMBER_HELP = 'Skriv et tal, fx 12,5 (uden tusindtalsseparator).'

function errorMessage(e: unknown): string {
  if (e instanceof ApiError && e.status === 422) {
    return `Én eller flere værdier har ugyldigt format. Ret felterne og prøv igen. ${NUMBER_HELP}`
  }
  if (e instanceof ApiError && e.status === 404) {
    return 'Felterne er ændret — genindlæs siden.'
  }
  return 'Kunne ikke gemme egne felter. Prøv igen.'
}

const headingStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em',
}

const controlStyle: React.CSSProperties = {
  width: '100%', maxWidth: 320, height: 36, padding: '0 10px', borderRadius: 7,
  border: '1px solid #e8ecf4', fontSize: 13, color: '#0f172a', background: '#fff',
  fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
}

/**
 * Landlord-defined custom fields for a property or lease.
 * basePath: e.g. `/api/v1/properties/{id}` or `/api/v1/leases/{id}`.
 */
export function CustomFieldsPanel({ basePath }: { basePath: string }) {
  // key resets all state (fields, draft, errors) when the target entity changes
  return <CustomFieldsPanelInner key={basePath} basePath={basePath} />
}

function CustomFieldsPanelInner({ basePath }: { basePath: string }) {
  const [fields, setFields] = React.useState<FieldValue[] | null>(null)
  const [draft, setDraft] = React.useState<Record<string, string>>({})
  const [saved, setSaved] = React.useState<Record<string, string>>({})
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [loadFailed, setLoadFailed] = React.useState(false)
  const [justSaved, setJustSaved] = React.useState(false)

  const apply = React.useCallback((list: FieldValue[]) => {
    const sorted = [...list].sort((a, b) => a.sort_order - b.sort_order)
    const d: Record<string, string> = {}
    sorted.forEach((f) => { d[f.definition_id] = toDraft(f) })
    setFields(sorted)
    setDraft(d)
    setSaved(d)
  }, [])

  React.useEffect(() => {
    apiFetch<FieldValue[]>(`${basePath}/custom-fields`)
      .then((list) => { setLoadFailed(false); apply(list) })
      .catch(() => setLoadFailed(true))
  }, [basePath, apply])

  if (loadFailed) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, marginTop: 20 }}>
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}>
          <span style={headingStyle}>Egne felter</span>
        </div>
        <p role="alert" style={{ padding: '14px 24px', margin: 0, fontSize: 13, color: '#dc2626' }}>
          Kunne ikke hente egne felter. Genindlæs siden og prøv igen.
        </p>
      </div>
    )
  }
  if (fields === null) return null

  if (fields.length === 0) {
    return (
      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, marginTop: 20 }}>
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}>
          <span style={headingStyle}>Egne felter</span>
        </div>
        <p style={{ padding: '14px 24px', margin: 0, fontSize: 13, color: '#94a3b8' }}>
          Opret egne felter under virksomhedens indstillinger (fanen «Egne felter»).
        </p>
      </div>
    )
  }

  const dirty = fields.some((f) => draft[f.definition_id] !== saved[f.definition_id])

  const setValue = (id: string, v: string) => {
    setDraft((prev) => ({ ...prev, [id]: v }))
    setJustSaved(false)
  }

  const handleSave = async () => {
    setError(null)
    const bad = fields.find(
      (f) => f.field_type === 'number' && (draft[f.definition_id] ?? '').trim() !== ''
        && !NUMBER_RE.test((draft[f.definition_id] ?? '').trim()),
    )
    if (bad) {
      setError(`Feltet «${bad.label}» er ikke et gyldigt tal. ${NUMBER_HELP}`)
      return
    }
    setSaving(true)
    try {
      const values = fields
        .filter((f) => draft[f.definition_id] !== saved[f.definition_id])
        .map((f) => {
          const raw = draft[f.definition_id] ?? ''
          let value: string | boolean | null
          if (raw.trim() === '') value = null
          else if (f.field_type === 'boolean') value = raw === 'true'
          else if (f.field_type === 'number') value = raw.trim().replace(',', '.') // Danish decimal comma -> API format, still a string
          else value = raw.trim()
          return { definition_id: f.definition_id, value }
        })
      const updated = await apiFetch<FieldValue[]>(`${basePath}/custom-fields`, {
        method: 'PUT',
        body: JSON.stringify({ values }),
      })
      apply(updated)
      setJustSaved(true)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, marginTop: 20 }}>
      <div style={{
        padding: '14px 24px', borderBottom: '1px solid #e8eaed',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
      }}>
        <span style={headingStyle}>Egne felter</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {justSaved && !dirty && <span style={{ fontSize: 12, color: '#16a34a' }}>Gemt</span>}
          <Button size="sm" onClick={handleSave} isLoading={saving} disabled={!dirty}>Gem</Button>
        </div>
      </div>

      <div style={{ padding: '8px 24px 16px' }}>
        <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 8px' }}>
          Skriv ikke personoplysninger i egne felter.
        </p>
        {fields.map((f) => {
          const id = `cf-${f.definition_id}`
          const val = draft[f.definition_id] ?? ''
          return (
            <div
              key={f.definition_id}
              style={{
                display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr',
                gap: 16, alignItems: 'center', padding: '8px 0',
              }}
            >
              <label htmlFor={id} style={{ fontSize: 13, color: '#6b7280', overflowWrap: 'anywhere' }}>
                {f.label}
              </label>
              {f.field_type === 'boolean' ? (
                <select id={id} value={val} onChange={(e) => setValue(f.definition_id, e.target.value)} style={controlStyle}>
                  <option value="">Ikke angivet</option>
                  <option value="true">Ja</option>
                  <option value="false">Nej</option>
                </select>
              ) : f.field_type === 'date' ? (
                <input id={id} type="date" value={val} onChange={(e) => setValue(f.definition_id, e.target.value)} style={controlStyle} />
              ) : f.field_type === 'number' ? (
                <input
                  id={id} type="text" inputMode="decimal" value={val} title={NUMBER_HELP}
                  onChange={(e) => setValue(f.definition_id, e.target.value)} style={controlStyle}
                />
              ) : (
                <input id={id} type="text" value={val} onChange={(e) => setValue(f.definition_id, e.target.value)} style={controlStyle} />
              )}
            </div>
          )
        })}
        {error && <p role="alert" style={{ fontSize: 12, color: '#dc2626', margin: '8px 0 0' }}>{error}</p>}
      </div>
    </div>
  )
}
